import { ChangeEvent, useState } from 'react';
import { useLang } from 'hooks/lang';

import {
  actions,
  loaders,
  TaskExcludedIds,
  isOwner,
  taskStatusByCode,
  getStatusConfig,
} from 'entities';

import {
  Modal,
  Skeleton,
  Input,
  Column,
  Row,
  Button,
  LabeledCheckbox,
  Table,
} from 'components/Blocks';
import { SaveIcon, XIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';
import { useUser } from 'hooks/user';

import { MESSAGES } from '../../utils';
import { getLastItem } from 'utils/array';
import { pick } from 'utils/object';

import { TaskOptionOverlayProps } from '../createTaskOptions';

export function ExcludedIdsModal({ close, isOpen, updateTask, task }: TaskOptionOverlayProps) {
  const { t } = useLang();
  const loggedUser = useUser();

  const updateOnTask = (excludedIds: TaskExcludedIds, canFinish = false) => {
    updateTask(
      pick(
        {
          excludedIds,
          canFinish,
        },
        ['excludedIds', canFinish ? 'canFinish' : 'excludedIds'],
      ),
    );
  };

  const { data: apiIds, errorOrLoading } = loaders.projects.taskExcludedIds.use({
    blockLoad: !task?.requireExcludedOWIds,

    params: { projectId: task?.projectId, taskId: task?.id },

    onDataChange: (excludedIds) => updateTask({ excludedIds }),
  });

  const [currentId, setCurrentId] = useState('');

  const excludedIds = task?.excludedIds;
  const missingFilling = !excludedIds?.noExclusionNecessary && !excludedIds?.ids.length;

  const { addToast } = useToast();

  const saveExcludedIds = actions.projects.saveTaskExcludedIds.use({
    block: missingFilling,

    onSuccess: (consolidated) => {
      if (excludedIds && consolidated.ids.length !== excludedIds?.ids.length)
        addToast(MESSAGES.excludedIdsMismatch(t));

      updateOnTask(consolidated, true);
    },
  });

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;

    const onlyNumbersAndSpaceRegex = /^[\d\s]*$/;

    if (!onlyNumbersAndSpaceRegex.test(value)) return;

    const lastCharTyped = getLastItem(value.split(''));

    if (lastCharTyped !== ' ' && !value.includes(' ')) return setCurrentId(value);

    const newIds = value.split(' ').filter(Boolean);

    updateOnTask({
      ...excludedIds!,
      ids: Array.from(new Set([...excludedIds!.ids, ...newIds])),
    });
    setCurrentId('');
  };

  const removeId = (id: string) => {
    updateOnTask({
      ...excludedIds!,
      ids: excludedIds!.ids.filter((existingId) => id !== existingId),
    });
  };

  const toggleExclusionNecessary = () => {
    setCurrentId('');

    updateOnTask({
      ...excludedIds!,
      noExclusionNecessary: !excludedIds?.noExclusionNecessary,
    });
  };

  const isTaskOwner = isOwner(task, loggedUser);

  const { isActiveStatus } = getStatusConfig('task', task);

  const canModify = isTaskOwner && isActiveStatus;

  const showNoExclusionToggle = excludedIds?.noExclusionNecessary || !excludedIds?.ids.length;

  const hasModification =
    !apiIds ||
    apiIds?.noExclusionNecessary !== excludedIds?.noExclusionNecessary ||
    apiIds?.ids.length !== excludedIds?.ids.length ||
    excludedIds?.ids.some((i) => !apiIds.ids.includes(i));

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      title={t('processing-refinement') as string}
      width="min(85vw, 670px)"
      description={t('please-add-all-ids-rejects-during-database-processing')}
      allowEscape={!hasModification}
    >
      {errorOrLoading ? (
        <>
          <Skeleton width="40%" height={32} />

          <Skeleton width="100%" height={48} />

          <Skeleton width="25%" height={42} />
        </>
      ) : (
        <Column gap="6">
          {canModify && !excludedIds?.noExclusionNecessary && (
            <>
              <Column gap="2">
                <span className="pl-1 text-xxs italic">
                  {t('press-space-to-add-the-id-to-the-list')}
                </span>

                <Input
                  disabled={!!excludedIds?.noExclusionNecessary}
                  value={currentId}
                  onChange={handleInputChange}
                />
              </Column>

              <Table
                items={(excludedIds?.ids ?? []).map((id) => ({ id }))}
                uniqueKey="id"
                className="rounded-lg border p-2"
                columns={[
                  { label: `ID (${excludedIds?.ids.length})`, property: 'id', sort: true },

                  {
                    hide: !canModify,

                    render: ({ id }) => (
                      <Button
                        variant="ghost"
                        color="gray"
                        onClick={() => removeId(id)}
                        className="ml-auto flex"
                      >
                        <XIcon size={12} />
                      </Button>
                    ),
                  },
                ]}
              />
            </>
          )}

          {canModify && (
            <Row justifyContent="space-between">
              {showNoExclusionToggle && (
                <LabeledCheckbox
                  checked={!!excludedIds?.noExclusionNecessary}
                  onChange={toggleExclusionNecessary}
                  disabled={!!excludedIds?.ids.length}
                  label={t('no-exclusion-was-necessary')}
                />
              )}

              <Button
                loading={saveExcludedIds.loading}
                disabled={
                  missingFilling ||
                  taskStatusByCode[task.status].blocks.updateIds ||
                  !hasModification
                }
                className="ml-auto"
                onClick={() =>
                  saveExcludedIds.execute(
                    pick(excludedIds!, ['ids', 'noExclusionNecessary', 'project', 'task']),
                  )
                }
              >
                <SaveIcon />
                {t('save')}
              </Button>
            </Row>
          )}
        </Column>
      )}
    </Modal>
  );
}
